import React, { useState, useCallback } from 'react';
import { useUserProfile } from '@/hooks/user/useUserProfile';
import { useFriendActions } from '@/hooks/useFriendActions';
import { UserInfo } from '@/api/types';
import { preventDuplicateClick } from '@/utils/my-utils';
import BaseDialog from './BaseDialog';
import { useFriendStore } from '@/stores/useFriendStore';
import { useChatActions } from '@/hooks/useChatActions';

interface UserSearchDialogProps {
  open: boolean;
  onClose: () => void;
  initialSearchQuery?: string;
}

/**
 * 用户搜索对话框
 * 提供用户搜索、展示结果、添加好友功能
 */
export default function UserSearchDialog({
  open,
  onClose,
  initialSearchQuery = ''
}: UserSearchDialogProps) {
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [searchResults, setSearchResults] = useState<UserInfo[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserInfo | null>(null);

  const { searchUsers, isLoading: searchLoading, error: searchError } = useUserProfile();
  const { addFriend, isLoading: addingFriend, error: addFriendError } = useFriendActions();

  const getFriendList = useFriendStore(state => state.getFriendList);

  const { getConversations } = useChatActions();

  /**
   * 执行搜索
   */
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;

    const resultList = await searchUsers(searchQuery.trim());
    if (resultList) {
      setSearchResults(resultList || []);
    }
  }, [searchQuery, searchUsers]);

  /**
   * 处理回车键搜索
   */
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  /**
   * 处理添加好友
   */
  const handleAddFriend = useCallback(
    preventDuplicateClick(async (userId: string) => {
      const success = await addFriend({ friend_id: userId });
      if (success) {
        // 刷新搜索结果，更新好友状态
        await handleSearch();
        setSelectedUser(null);

        getFriendList();
        getConversations();
      }
    }),
    [addFriend, handleSearch]
  );

  /**
   * 渲染用户卡片
   */
  const renderUserCard = (user: UserInfo) => (
    <div key={user.id} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
      {/* 头像 */}
      <div className="flex-shrink-0">
        {user.avatar ? (
          <img
            src={user.avatar}
            alt={user.nickname}
            className="h-12 w-12 rounded-full object-cover"
          />
        ) : (
          <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center">
            <span className="text-gray-600 font-medium">
              {user.nickname?.charAt(0)?.toUpperCase() || '?'}
            </span>
          </div>
        )}
      </div>

      {/* 用户信息 */}
      <div className="ml-3 flex-1">
        <div className="flex items-center">
          <p className="text-sm font-medium text-gray-900">{user.nickname}</p>
          {user.handle_rand && (
            <span className="ml-2 text-xs text-gray-500">#{user.handle_rand}</span>
          )}
        </div>
        {user.bio && (
          <p className="text-xs text-gray-500 mt-1 max-w-[200px] truncate text-ellipsis">{user.bio}</p>
        )}
      </div>

      {/* 操作按钮 */}
      <div className="shrink-0 ml-3 flex space-x-2">
        <button
          onClick={() => setSelectedUser(user)}
          className="px-3 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 border border-blue-300 hover:border-blue-400 rounded transition-colors"
        >
          查看详情
        </button>
        {!user.is_friend && (
          <button
            onClick={() => handleAddFriend(user.id)}
            disabled={addingFriend}
            className="px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 rounded transition-colors"
          >
            {addingFriend ? '添加中...' : '添加好友'}
          </button>
        )}
        {user.is_friend && (
          <span className="px-3 py-1 text-xs font-medium text-green-600 bg-green-100 rounded">
            已添加
          </span>
        )}
      </div>
    </div>
  );

  /**
   * 渲染用户详情
   */
  const renderUserDetail = (user: UserInfo) => (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      <div className="flex items-start">
        {/* 头像 */}
        <div className="flex-shrink-0">
          {user.avatar ? (
            <img
              src={user.avatar}
              alt={user.nickname}
              className="h-16 w-16 rounded-full object-cover"
            />
          ) : (
            <div className="h-16 w-16 rounded-full bg-gray-300 flex items-center justify-center">
              <span className="text-gray-600 font-medium text-lg">
                {user.nickname?.charAt(0)?.toUpperCase() || '?'}
              </span>
            </div>
          )}
        </div>

        {/* 详细信息 */}
        <div className="ml-4 flex-1">
          <div className="flex items-center">
            <h4 className="text-lg font-medium text-gray-900">{user.nickname}</h4>
            {user.handle_rand && (
              <span className="ml-2 text-sm text-gray-500">#{user.handle_rand}</span>
            )}
          </div>

          {user.bio && (
            <p className="text-sm text-gray-600 mt-1">{user.bio}</p>
          )}

          {user.status && (
            <div className="flex items-center mt-2">
              <div className={`h-2 w-2 rounded-full mr-2 ${user.status === 'online' ? 'bg-green-400' :
                user.status === 'away' ? 'bg-yellow-400' :
                  user.status === 'busy' ? 'bg-red-400' : 'bg-gray-400'
                }`} />
              <span className="text-xs text-gray-500 capitalize">{user.status}</span>
            </div>
          )}
        </div>
      </div>

      {/* 操作按钮 */}
      <div className="flex justify-end space-x-2 mt-4">
        <button
          onClick={() => setSelectedUser(null)}
          className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 border border-gray-300 hover:border-gray-400 rounded transition-colors"
        >
          返回
        </button>
        {!user.is_friend && (
          <button
            onClick={() => handleAddFriend(user.id)}
            disabled={addingFriend}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 rounded transition-colors"
          >
            {addingFriend ? '添加中...' : '添加好友'}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title="搜索用户"
      maxWidth="lg"
    >
      <div className="space-y-4">
        {/* 搜索输入框 */}
        <div className="flex space-x-2">
          <input
            type="text"
            placeholder="请输入用户ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          <button
            onClick={handleSearch}
            disabled={searchLoading || !searchQuery.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 rounded-lg transition-colors"
          >
            {searchLoading ? '搜索中...' : '搜索'}
          </button>
        </div>

        {/* 错误信息 */}
        {(searchError || addFriendError) && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{searchError || addFriendError}</p>
          </div>
        )}

        {/* 搜索结果 */}
        {!selectedUser && (
          <div className="space-y-3">
            {searchResults.length > 0 ? (
              <>
                <p className="text-sm text-gray-600">找到 {searchResults.length} 个用户:</p>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {searchResults.map(renderUserCard)}
                </div>
              </>
            ) : null}
          </div>
        )}

        {/* 用户详情 */}
        {selectedUser && renderUserDetail(selectedUser)}
      </div>
    </BaseDialog>
  );
}
